/**
 * Unified from–to linking for datagrid filter ranges and DateRangeControl.
 * Changed field wins; the other is clamped. Pickers get min/max so invalid
 * values cannot be selected.
 */

export type RangeType = 'date' | 'datetime' | 'time' | 'integer' | 'text';

const LINKED_ATTR = 'data-nais-range-linked';

/** Inputs currently inside a sync cycle (avoids datepicker option/change recursion). */
const syncingInputs = new WeakSet<HTMLInputElement>();

function findPair(wrap: HTMLElement): { from: HTMLInputElement; to: HTMLInputElement } | null {
    const inputs = Array.from(
        wrap.querySelectorAll<HTMLInputElement>(
            'input.form-control, input.datepicker, input.datetimepicker, input.timepicker, input.dateRangePicker',
        ),
    ).filter((el) => el.type !== 'hidden' && el.type !== 'checkbox' && el.type !== 'radio');

    const fromById = inputs.find((i) => i.id.endsWith('-from'));
    const toById = inputs.find((i) => i.id.endsWith('-to'));
    if (fromById && toById) {
        return { from: fromById, to: toById };
    }

    const fromByName = inputs.find((i) =>
        /(_DateFrom|_DateTimeFrom|_TimeFrom|_IntegerFrom|_TextFrom|\[from\])\s*$/i.test(i.name),
    );
    const toByName = inputs.find((i) =>
        /(_DateTo|_DateTimeTo|_TimeTo|_IntegerTo|_TextTo|\[to\])\s*$/i.test(i.name),
    );
    if (fromByName && toByName) {
        return { from: fromByName, to: toByName };
    }

    if (inputs.length >= 2) {
        return { from: inputs[0], to: inputs[1] };
    }

    return null;
}

function dateFormatOf(el: HTMLInputElement): string {
    return el.getAttribute('dateformat')
        || el.getAttribute('jsdateformat')
        || 'dd.mm.yy';
}

function parseTemporal($: JQueryStatic, el: HTMLInputElement, type: RangeType): Date | null {
    const raw = el.value?.trim();
    if (!raw) {
        return null;
    }

    try {
        const $el = $(el);
        if ($el.hasClass('hasDatepicker') && typeof $el.datepicker === 'function') {
            const d = $el.datepicker('getDate') as Date | null;
            if (d instanceof Date && !Number.isNaN(d.getTime())) {
                return d;
            }
        }

        if (type === 'time') {
            const m = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
            if (!m) {
                return null;
            }
            const d = new Date(1970, 0, 1, Number(m[1]), Number(m[2]), Number(m[3] || 0));
            return Number.isNaN(d.getTime()) ? null : d;
        }

        if ($.datepicker?.parseDate) {
            return $.datepicker.parseDate(dateFormatOf(el), raw) as Date;
        }
    } catch {
        return null;
    }

    return null;
}

function setTemporal($: JQueryStatic, el: HTMLInputElement, date: Date, type: RangeType): void {
    const $el = $(el);
    if ($el.hasClass('hasDatepicker') && typeof $el.datepicker === 'function') {
        // setDate fires change — caller must hold syncingInputs
        $el.datepicker('setDate', date);
        return;
    }

    if (type === 'time') {
        const pad = (n: number) => String(n).padStart(2, '0');
        el.value = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
        return;
    }

    if ($.datepicker?.formatDate) {
        el.value = $.datepicker.formatDate(dateFormatOf(el), date) as string;
    }
}

function applyPickerLimit(
    $: JQueryStatic,
    el: HTMLInputElement,
    option: 'minDate' | 'maxDate' | 'minTime' | 'maxTime',
    value: Date | string | null,
): void {
    const $el = $(el);
    if (!$el.hasClass('hasDatepicker') || typeof $el.datepicker !== 'function') {
        return;
    }

    // Reading current option avoids redundant sets that can re-enter datepicker internals.
    try {
        const current = $el.datepicker('option', option);
        if (current === value) {
            return;
        }
        if (
            value instanceof Date
            && current instanceof Date
            && current.getTime() === value.getTime()
        ) {
            return;
        }
    } catch {
        // ignore — still try to set
    }

    $el.datepicker('option', option, value);
}

function formatTimeValue(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function beginSync(from: HTMLInputElement, to: HTMLInputElement): boolean {
    if (syncingInputs.has(from) || syncingInputs.has(to)) {
        return false;
    }
    syncingInputs.add(from);
    syncingInputs.add(to);
    return true;
}

function endSync(from: HTMLInputElement, to: HTMLInputElement): void {
    // Clear on next tick — datepicker may emit change asynchronously after option/setDate.
    window.setTimeout(() => {
        syncingInputs.delete(from);
        syncingInputs.delete(to);
    }, 0);
}

function linkTemporalPair(
    $: JQueryStatic,
    from: HTMLInputElement,
    to: HTMLInputElement,
    type: RangeType,
): void {
    const syncLimits = (source: 'from' | 'to'): void => {
        if (!beginSync(from, to)) {
            return;
        }

        try {
            let fromDate = parseTemporal($, from, type);
            let toDate = parseTemporal($, to, type);

            // Clamp first (changed field wins), then set limits from final values.
            // Setting maxDate < current value before clamp makes datepicker adjust the
            // field and re-fire change → recursion.
            if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
                if (source === 'from') {
                    setTemporal($, to, fromDate, type);
                    toDate = fromDate;
                } else {
                    setTemporal($, from, toDate, type);
                    fromDate = toDate;
                }
            }

            if (type === 'time') {
                applyPickerLimit($, to, 'minTime', fromDate ? formatTimeValue(fromDate) : null);
                applyPickerLimit($, from, 'maxTime', toDate ? formatTimeValue(toDate) : null);
            } else {
                applyPickerLimit($, to, 'minDate', fromDate);
                applyPickerLimit($, from, 'maxDate', toDate);
            }
        } finally {
            endSync(from, to);
        }
    };

    $(from).off('.naisRange').on('change.naisRange', () => syncLimits('from'));
    $(to).off('.naisRange').on('change.naisRange', () => syncLimits('to'));

    // Initial limits only when at least one side has a value
    if (from.value?.trim() || to.value?.trim()) {
        syncLimits('from');
    }
}

function linkIntegerPair($: JQueryStatic, from: HTMLInputElement, to: HTMLInputElement): void {
    const parseIntValue = (el: HTMLInputElement): number | null => {
        const raw = el.value?.trim();
        if (raw === '' || raw === undefined) {
            return null;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    };

    const sync = (source: 'from' | 'to'): void => {
        if (!beginSync(from, to)) {
            return;
        }

        try {
            const fromVal = parseIntValue(from);
            const toVal = parseIntValue(to);

            if (fromVal !== null) {
                to.min = String(fromVal);
            } else {
                to.removeAttribute('min');
            }

            if (toVal !== null) {
                from.max = String(toVal);
            } else {
                from.removeAttribute('max');
            }

            if (fromVal !== null && toVal !== null && fromVal > toVal) {
                if (source === 'from') {
                    to.value = String(fromVal);
                    from.max = String(fromVal);
                } else {
                    from.value = String(toVal);
                    to.min = String(toVal);
                }
            }
        } finally {
            endSync(from, to);
        }
    };

    $(from).off('.naisRange').on('input.naisRange change.naisRange', () => sync('from'));
    $(to).off('.naisRange').on('input.naisRange change.naisRange', () => sync('to'));

    if (from.value?.trim() || to.value?.trim()) {
        sync('from');
    }
}

function linkTextPair($: JQueryStatic, from: HTMLInputElement, to: HTMLInputElement): void {
    const sync = (source: 'from' | 'to'): void => {
        if (!beginSync(from, to)) {
            return;
        }

        try {
            const fromVal = from.value ?? '';
            const toVal = to.value ?? '';
            if (fromVal === '' || toVal === '') {
                return;
            }

            if (fromVal > toVal) {
                if (source === 'from') {
                    to.value = fromVal;
                } else {
                    from.value = toVal;
                }
            }
        } finally {
            endSync(from, to);
        }
    };

    $(from).off('.naisRange').on('change.naisRange blur.naisRange', () => sync('from'));
    $(to).off('.naisRange').on('change.naisRange blur.naisRange', () => sync('to'));
}

function linkWrap($: JQueryStatic, wrap: HTMLElement): void {
    const type = (wrap.getAttribute('data-nais-range') || 'text') as RangeType;
    const pair = findPair(wrap);
    if (!pair) {
        return;
    }

    if (type === 'integer') {
        linkIntegerPair($, pair.from, pair.to);
    } else if (type === 'text') {
        linkTextPair($, pair.from, pair.to);
    } else {
        linkTemporalPair($, pair.from, pair.to, type);
    }

    wrap.setAttribute(LINKED_ATTR, '1');
}

/**
 * Link all [data-nais-range] pairs in root. Safe to call repeatedly
 * (rebinds namespaced handlers and refreshes picker min/max).
 * Call after initCalendars so pickers exist.
 */
export function initRangeLinks(root: ParentNode | Document = document): void {
    const $ = window.jQuery;
    if (!$) {
        return;
    }

    root.querySelectorAll<HTMLElement>('[data-nais-range]').forEach((wrap) => {
        linkWrap($, wrap);
    });
}

window.initRangeLinks = initRangeLinks;
